import React, { Fragment, useEffect, useState } from "react";
import { Row, Col, Card, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { MecordColors } from "Constants/Color";
import { showCustomSwal } from "helpers/swalHelper";
import StatusBadge from "Common/Components/StatusBadge/StatusBadge";
import Colors from "Constants/Color";
import { useDispatch } from "react-redux";
import {
  approveRejectPurchaseRequest,
  fetchPurchaseRequestId,
} from "slices/Procurement/purchase-request/thunk";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import LoadingSpinner from "components/LoadingSpinner";
import { AttachmentList } from "components/DocumentListing/AttachmentListing";
import Tooltip from "@mui/material/Tooltip";

const PurchaseRequestApproveForm: React.FC = () => {
  const [remark, setRemark] = useState<string>("");
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [submittingAction, setSubmittingAction] = useState<
    "approve" | "reject" | null
  >(null);

  const { id } = useParams();
  const [data, setData] = useState<any>({});
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  useEffect(() => {
    if (id) {
      setPageLoading(true);
      dispatch(fetchPurchaseRequestId(id))
        .unwrap()
        .then((res: any) => {
          setData(res.data);
          if (res.data && res.data.pr_approval_remark) {
            setRemark(res.data.pr_approval_remark);
          }
        })
        .catch((e: any) => {
          toast.error(e?.message || "Something went wrong");
        })
        .finally(() => {
          setPageLoading(false);
        });
    }
  }, [id, dispatch]);

  const displayValue = (val: any) => val || "-";

  const statusLower = String(data?.status ?? "").trim().toLowerCase();
  const isApproved = statusLower === "approved";
  const isRejected = statusLower === "rejected";

  const handleApproveReject = async (action: "approve" | "reject") => {
    if (!data?.pr_id) return;
    if (action === "approve" && isApproved) return;
    if (action === "reject" && isRejected) return;

    setSubmittingAction(action);
    try {
      await dispatch(
        approveRejectPurchaseRequest({
          pr_id: data.pr_id,
          action,
          remark: remark.trim() || undefined,
        }),
      ).unwrap();
      toast.success(
        action === "approve"
          ? "Purchase Request approved successfully"
          : "Purchase Request rejected successfully",
      );
      navigate("/purchase-management/purchase-request");
    } catch (e: any) {
      handleSubmissionError(e);
    } finally {
      setSubmittingAction(null);
    }
  };

  const handleSubmissionError = (err: any) => {
    if (typeof err === "string") {
      toast.error(err);
      return;
    }
    if (err && typeof err === "object") {
      const backendMessage = err?.message;
      const errType = err?.errors || {};
      if (errType && Object.keys(errType).length > 0) {
        Object.keys(errType).forEach((key: string) => {
          const msg = Array.isArray((errType as any)[key])
            ? (errType as any)[key][0]
            : JSON.stringify((errType as any)[key]);
          showCustomSwal({ icon: "error", text: `${msg}` });
        });
        return;
      }
      if (backendMessage) {
        toast.error(backendMessage);
        return;
      }
    }
    toast.error("Something went wrong. Please try again.");
  };

  return (
    <Fragment>
      <PageContainer>
        <PageBreadcrumb
          title="Approve Purchase Request"
          titleLink="/purchase-management/purchase-request"
          breadcrumbs={[
            {
              label: "Purchase Request",
              to: "/purchase-management/purchase-request",
            },
            { label: "Approve Purchase Request", to: "" },
          ]}
          backNavigationPath={-1}
        />
        <hr />

        {pageLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <p className="mb-3">
                  <strong>PR No. :</strong> {displayValue(data?.pr_no)}
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-3">
                  <strong>Status :</strong>{" "}
                  <StatusBadge
                    status={data?.status || "Pending"}
                    bgColor={
                      data?.status === "Approved"
                        ? Colors.bg_success
                        : data?.status === "Rejected"
                          ? Colors.bg_damage
                          : data?.status === "PO Created"
                            ? Colors.bg_blue
                            : data?.status === "Partial PO Created"
                              ? "#6366F1"
                              : Colors.bg_yellow
                    }
                    color="#fff"
                  />
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-3">
                  <strong>PR Date :</strong>{" "}
                  <FormattedDate date={data?.pr_date} />
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-3">
                  <strong>Initiated By :</strong>{" "}
                  {displayValue(data?.initiated_by)}
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-3">
                  <strong>Tags :</strong>{" "}
                  {Array.isArray(data?.tags) && data.tags.length > 0 ? (
                    <span className="d-flex flex-wrap gap-2 mt-1">
                      {data.tags.map((tag: string) => (
                        <span
                          key={tag}
                          className="badge bg-light text-dark rounded-2"
                          style={{
                            border: "1px solid #dee2e6",
                            padding: "6px 10px",
                            fontSize: "0.8rem",
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </span>
                  ) : (
                    "-"
                  )}
                </p>
              </Col>
            </Row>

            <div className="mb-4">
              <h6 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                Products
              </h6>
              <Card
                style={{
                  boxShadow: "none",
                  backgroundColor: MecordColors?.LightThemeBGColor,
                }}
              >
                <Card.Body className="m-0 p-3">
                  <div style={{ overflowX: "auto" }}>
                    <table className="table align-middle">
                      <thead
                        style={{
                          backgroundColor: MecordColors.LightGrayColor,
                        }}
                      >
                        <tr>
                          <th style={{ minWidth: "60px" }}>Sr No.</th>
                          <th style={{ minWidth: "180px" }}>Product Name</th>
                          <th style={{ minWidth: "120px" }}>Product Code</th>
                          <th style={{ minWidth: "100px" }}>Quantity</th>
                          <th style={{ minWidth: "120px" }}>Unit</th>
                          <th style={{ minWidth: "120px" }}>Unit Price</th>
                          <th style={{ minWidth: "120px" }}>Amount</th>
                          <th style={{ minWidth: "100px" }}>Status</th>
                          <th style={{ minWidth: "180px" }}>Description</th>
                        </tr>
                      </thead>
                      <tbody style={{ backgroundColor: "#ffffff" }}>
                        {data?.products?.map((item: any, i: number) => {
                          const productName =
                            item?.product?.product_name ||
                            item?.product_details?.product_name ||
                            "-";
                          const unitName =
                            item?.product?.unit?.unit_name ||
                            item?.product_details?.unit_name ||
                            "-";
                          const qty = Number(item?.quantity) || 0;
                          const unitPrice = Number(item?.unit_price) || 0;
                          const amount = qty * unitPrice;
                          return (
                            <tr key={item?.pr_product_id || i}>
                              <td>{i + 1}</td>
                              <td>{displayValue(productName)}</td>
                              <td>{displayValue(item?.product_code)}</td>
                              <td>{displayValue(item?.quantity)}</td>
                              <td>{displayValue(unitName)}</td>
                              <td>{displayValue(unitPrice)}</td>
                              <td>{displayValue(amount)}</td>
                              <td>
                                <StatusBadge
                                  status={item?.status || "Pending"}
                                  bgColor={
                                    item?.status === "PO Created"
                                      ? Colors.bg_blue
                                      : item?.status === "Partial PO Created"
                                        ? "#6366F1"
                                        : Colors.bg_yellow
                                  }
                                  color="#fff"
                                />
                              </td>
                              <td>
                                <Tooltip
                                  title={item?.description || ""}
                                  arrow
                                  placement="top"
                                >
                                  <span
                                    style={{
                                      maxWidth: "200px",
                                      display: "inline-block",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      verticalAlign: "middle",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {displayValue(item?.description)}
                                  </span>
                                </Tooltip>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {(!data?.products || data.products.length === 0) && (
                      <p className="text-muted text-center py-4 mb-0">
                        No products added
                      </p>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </div>

            {/* {canApproveReject && ( */}
            <div className="mb-3">
              <Form.Label>
                {data?.status === "Rejected"
                  ? "Rejection Remark:"
                  : "Approval Remark:"}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="remark"
                placeholder="Enter remark (optional)"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
            </div>
            {/* )} */}

            <Row>
              <Col md={6}>
                <p className="mb-3">
                  <strong>Notes :</strong>
                  <div style={{ marginLeft: "10px", whiteSpace: "pre-wrap" }}>
                    {displayValue(data?.notes)}
                  </div>
                </p>
              </Col>
              <Col md={6}>
                <strong>PR Attachments :</strong>
                <AttachmentList attachments={data?.attachments} />
              </Col>
            </Row>

            <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-5">
              <PrimarySubmitButton
                label="Reject"
                type="button"
                onClick={() => handleApproveReject("reject")}
                isLoading={submittingAction === "reject"}
                loadingLabel="Rejecting..."
                disabled={!!submittingAction || isRejected}
              />
              <CancelButton
                onClick={() =>
                  navigate("/purchase-management/purchase-request")
                }
                disabled={!!submittingAction}
                showConfirmation={false}
              />
              <PrimarySubmitButton
                label="Approve"
                type="button"
                onClick={() => handleApproveReject("approve")}
                isLoading={submittingAction === "approve"}
                loadingLabel="Approving..."
                disabled={!!submittingAction || isApproved}
                variantColor={Colors.bg_success}
              />
            </div>
          </>
        )}
      </PageContainer>
    </Fragment>
  );
};

export default PurchaseRequestApproveForm;
